import React, { useState, useCallback, useEffect } from 'react';
import { Row, Col, Button, Label, FormGroup, Input } from 'reactstrap';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

import ReactSelect from 'components/Form/Select/Select';
import Loading from 'lib/loading';
import rotas from 'services/rotas';
import rest from 'lib/rest';

const formSwal = withReactContent(Swal);

/* ─── helpers ─── */
const fmtNum = (v) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const fmtInt = (v) => new Intl.NumberFormat('pt-BR').format(v || 0);

const TIPO_FILTRO_OPTS = [
  { id: 'TODOS',        title: 'Todos os produtos' },
  { id: 'CATEGORIA',    title: 'Categoria'         },
  { id: 'SUBCATEGORIA', title: 'Subcategoria'      },
  { id: 'FORNECEDOR',   title: 'Fornecedor'        },
];

const PRECO_BASE_OPTS = [
  { value: 'MENOR',    label: 'Menor preço'       },
  { value: 'PROMOCAO', label: 'Preço de Promoção' },
  { value: 'VENDA',    label: 'Preço de Venda'    },
];

const CURVA_STYLE = {
  A: { bg: '#ECFDF5', color: '#065f46' },
  B: { bg: '#FFFBEB', color: '#92400e' },
  C: { bg: '#FEF2F2', color: '#991b1b' },
  D: { bg: '#f3f4f6', color: '#374151' },
};

/* Cor do vencimento baseada em lote_dias */
const vencColor = (dias) => {
  if (dias <= 0)  return { color: '#94a3b8', fontWeight: 400, background: '#f1f5f9', borderRadius: 4, padding: '1px 5px' }; // vencido
  if (dias <= 30) return { color: '#dc2626', fontWeight: 700, background: '#fee2e2', borderRadius: 4, padding: '1px 5px' }; // crítico
  if (dias <= 90) return { color: '#d97706', fontWeight: 700, background: '#fef3c7', borderRadius: 4, padding: '1px 5px' }; // atenção
  return { color: '#065f46', fontWeight: 400 };                                                                              // ok
};

/* ══════════════════════════════════════════ */
const EmMassaTab = ({ campanha_id, filial, listaLoja, onFilialChange }) => {

  /* ── filtros ── */
  const [tipoFiltro,      setTipoFiltro]      = useState(TIPO_FILTRO_OPTS[0]);
  const [filtroOpcoes,    setFiltroOpcoes]    = useState([]);
  const [filtroId,        setFiltroId]        = useState(null);
  const [apenasComSaldo,  setApenasComSaldo]  = useState(true);
  const [liberados,       setLiberados]       = useState(true);
  const [controlados,     setControlados]     = useState(true);
  const [vencerAtivo,     setVencerAtivo]     = useState(false);
  const [vencerDias,      setVencerDias]      = useState(30);
  const [especificarLotes,setEspecificarLotes]= useState(false);
  const [precoBase,       setPrecoBase]       = useState('MENOR');

  /* ── resultados ── */
  const [lista,         setLista]         = useState([]);
  const [prCampanha,    setPrCampanha]    = useState({});
  const [selecionados,  setSelecionados]  = useState(new Set());
  const [apenasMarcados,setApenasMarcados]= useState(false);

  /* ── alterar em lote ── */
  const [alterarTipo,  setAlterarTipo]  = useState('FIXO');
  const [alterarValor, setAlterarValor] = useState('');

  /* ── status ── */
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingSave,   setLoadingSave]   = useState(false);
  const [pesquisou,     setPesquisou]     = useState(false);

  /* ─── Chave única da linha (seleção) e chave de preço (sempre por produto) ─── */
  const rowKey   = (prod) => especificarLotes ? `l_${prod.lote_id}` : `p_${prod.produto_id}`;
  const priceKey = (prod) => `p_${prod.produto_id}`;

  /* ─── Limpa resultados ao trocar de loja ─── */
  useEffect(() => {
    setLista([]);
    setPesquisou(false);
    setSelecionados(new Set());
    setPrCampanha({});
  }, [filial]);

  /* ─── Carrega opções do 2° dropdown ─── */
  const carregarFiltroOpcoes = useCallback(async (tipo) => {
    setFiltroId(null);
    setFiltroOpcoes([]);
    if (tipo === 'TODOS') return;
    try {
      let ret;
      if (tipo === 'CATEGORIA') {
        ret = await rest.get(rotas.categoriaSelect);
        if (ret.success === 'S' && ret.lista)
          setFiltroOpcoes(ret.lista.map((c) => ({ id: c.categoria_id, title: c.categoria })));
      } else if (tipo === 'SUBCATEGORIA') {
        ret = await rest.get(rotas.subcategoria);
        if (ret.success === 'S' && ret.lista)
          setFiltroOpcoes(ret.lista.map((s) => ({ id: s.subcategoria_id, title: s.subcategoria })));
      } else if (tipo === 'FORNECEDOR') {
        if (!filial) {
          formSwal.fire('Atenção', 'Selecione uma loja antes de filtrar por fornecedor.', 'warning');
          setTipoFiltro(TIPO_FILTRO_OPTS[0]);
          return;
        }
        ret = await rest.get(rotas.fornecedor + '?filial_id=' + filial.id);
        if (ret.success === 'S' && ret.lista)
          setFiltroOpcoes(ret.lista.map((f) => ({ id: f.fornecedor_id, title: f.fornecedor || f.razao_social })));
      }
    } catch (e) { console.error(e); }
  }, [filial]);

  const handleTipoFiltroChange = (opt) => {
    setTipoFiltro(opt);
    carregarFiltroOpcoes(opt.id);
  };

  /* ─── Pesquisar ─── */
  const pesquisar = () => {
    if (!filial) {
      formSwal.fire('Atenção', 'Selecione uma loja antes de pesquisar.', 'warning');
      return;
    }
    if (!liberados && !controlados) {
      formSwal.fire('Atenção', 'Selecione ao menos um tipo de produto.', 'warning');
      return;
    }
    setLoadingSearch(true);
    setSelecionados(new Set());
    setPesquisou(false);

    rest.post(rotas.campanhaPesquisaMassa, {
      form: {
        filial_id:         filial.id,
        tipo_filtro:       tipoFiltro.id,
        filtro_id:         filtroId?.id || 0,
        saldo:             apenasComSaldo    ? 'S' : 'N',
        liberados:         liberados         ? 'S' : 'N',
        controlados:       controlados       ? 'S' : 'N',
        vencer_dias:       vencerAtivo       ? vencerDias : 0,
        especificar_lotes: especificarLotes  ? 'S' : 'N',
        preco_base:        precoBase,
        campanha_id,
      },
    }).then((ret) => {
      setLoadingSearch(false);
      setPesquisou(true);
      if (ret.success === 'S') {
        const nova = ret.lista || [];
        setLista(nova);
        const precos = {};
        nova.forEach((p) => {
          // preço sempre por produto (priceKey), evita duplicidade em modo lotes
          const pk = `p_${p.produto_id}`;
          if (!(pk in precos)) {
            precos[pk] = Math.round(p.pr_campanha * 100) / 100;
          }
        });
        setPrCampanha(precos);
      } else {
        formSwal.fire('Erro', ret.msg || 'Erro ao pesquisar produtos.', 'error');
      }
    }).catch(() => {
      setLoadingSearch(false);
      formSwal.fire('Erro', 'Falha na comunicação.', 'error');
    });
  };

  /* ─── Seleção ─── */
  const toggleSel = (key) =>
    setSelecionados((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const marcarTodos = () =>
    setSelecionados((prev) => {
      const next = new Set(prev);
      listaVisivel.forEach((p) => next.add(rowKey(p)));
      return next;
    });

  const desmarcarTodos = () =>
    setSelecionados((prev) => {
      const next = new Set(prev);
      listaVisivel.forEach((p) => next.delete(rowKey(p)));
      return next;
    });

  /* ─── Alterar em lote ─── */
  const aplicarAlteracao = () => {
    const val = parseFloat(String(alterarValor).replace(',', '.')) || 0;
    if (val <= 0) { formSwal.fire('Atenção', 'Informe um valor maior que zero.', 'warning'); return; }
    if (!selecionados.size) { formSwal.fire('Atenção', 'Selecione ao menos um produto.', 'warning'); return; }

    setPrCampanha((prev) => {
      const next = { ...prev };
      selecionados.forEach((key) => {
        const atual = parseFloat(next[key]) || 0;
        next[key] = alterarTipo === 'FIXO'
          ? Math.max(0, Math.round((atual - val) * 100) / 100)
          : Math.max(0, Math.round((atual - (atual * val) / 100) * 100) / 100);
      });
      return next;
    });
  };

  /* ─── Salvar ─── */
  const salvar = () => {
    if (!selecionados.size) { formSwal.fire('Atenção', 'Selecione ao menos um produto.', 'warning'); return; }

    /* Agrupar por produto_id; em modo lotes, coletar todos os lotes selecionados por produto */
    const itensMap = new Map();
    lista
      .filter((p) => selecionados.has(rowKey(p)))
      .forEach((p) => {
        if (!itensMap.has(p.produto_id)) {
          itensMap.set(p.produto_id, {
            produto_id:        p.produto_id,
            campanha_itens_id: p.campanha_itens_id || 0,
            valor:             parseFloat(prCampanha[priceKey(p)]) || 0,
            lotes:             [],
          });
        }
        if (especificarLotes && p.lote_id) {
          itensMap.get(p.produto_id).lotes.push({
            lote_id:  p.lote_id,
            num_lote: p.lote || '',
          });
        }
      });

    const itens = [...itensMap.values()].filter((i) => i.valor > 0);

    if (!itens.length) { formSwal.fire('Atenção', 'Todos os produtos selecionados estão com valor zerado.', 'warning'); return; }

    setLoadingSave(true);
    rest.post(rotas.campanhaInserirEmLote, {
      form: { campanha_id, filial_id: filial.id, itens },
    }).then((ret) => {
      setLoadingSave(false);
      if (ret.success === 'S') {
        formSwal.fire('Sucesso!', `${ret.qtd_inseridos} inserido(s) · ${ret.qtd_atualizados} atualizado(s)`, 'success');
        pesquisar();
      } else {
        formSwal.fire('Erro', ret.msg || 'Erro ao salvar.', 'error');
      }
    }).catch(() => {
      setLoadingSave(false);
      formSwal.fire('Erro', 'Falha na comunicação.', 'error');
    });
  };

  /* ─── lista visível ─── */
  const listaVisivel = apenasMarcados
    ? lista.filter((p) => selecionados.has(rowKey(p)))
    : lista;

  const todosVisveisMarcados =
    listaVisivel.length > 0 && listaVisivel.every((p) => selecionados.has(rowKey(p)));

  /* ══ render ══ */
  return (
    <div>
      <style>{`
        .em-tr:hover td { background: #f8fafc !important; cursor: pointer; }
        .em-tr-sel td   { background: #fff8f8 !important; }
        .em-table th.em-col-produto,
        .em-table td.em-col-produto { text-align: left !important; }
        .em-price {
          width: 82px; text-align: right;
          border: 1px solid #e2e8f0; border-radius: 4px;
          padding: 2px 5px; font-size: 12px; font-weight: 700;
          color: #065f46; outline: none;
        }
        .em-price:focus { border-color: #e74c3c; }
      `}</style>

      {/* ── Filtros ── */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px', marginBottom: 14 }}>

        {/* Linha 1: Loja · Apenas saldo · Pesquisar */}
        <Row className="align-items-end mb-2">
          <Col md="5">
            <Label style={{ fontSize: 11, fontWeight: 600, color: '#7f8c8d', marginBottom: 3 }}>
              Loja <span style={{ color: '#e74c3c' }}>*</span>
            </Label>
            <ReactSelect
              name="emMassaFilial" placeholder="Selecione..."
              options={listaLoja} value={filial}
              onChange={onFilialChange}
            />
          </Col>

          <Col md="4" className="d-flex align-items-end" style={{ paddingBottom: 6 }}>
            <FormGroup check inline className="mb-0">
              <Label check>
                <Input type="checkbox" checked={apenasComSaldo}
                  onChange={(e) => setApenasComSaldo(e.target.checked)} />
                <span className="form-check-sign" />Apenas produtos com saldo
              </Label>
            </FormGroup>
          </Col>

          <Col md="3" className="d-flex align-items-end">
            <Button color="danger" block onClick={pesquisar} className="mb-0"
              disabled={loadingSearch || !filial} style={{ height: 38 }}>
              {loadingSearch
                ? <><i className="fa fa-spinner fa-spin mr-1" />Buscando...</>
                : <><i className="fa fa-search mr-1" />Pesquisar</>}
            </Button>
          </Col>
        </Row>

        <hr className="my-2" />

        {/* Linha 2: Tipo de filtro · Sub-dropdown · Tipo de produto */}
        <Row className="align-items-end mb-2">
          <Col md="3">
            <Label style={{ fontSize: 11, fontWeight: 600, color: '#7f8c8d', marginBottom: 3 }}>Tipo de filtro</Label>
            <ReactSelect
              name="emMassaTipo" options={TIPO_FILTRO_OPTS}
              value={tipoFiltro} onChange={handleTipoFiltroChange}
            />
          </Col>

          <Col md="3">
            <Label style={{ fontSize: 11, fontWeight: 600, color: '#7f8c8d', marginBottom: 3 }}>
              {tipoFiltro.id === 'TODOS' ? 'Valor' : tipoFiltro.title}
            </Label>
            <ReactSelect
              name="emMassaFiltroId" placeholder="Todos..."
              options={filtroOpcoes} value={filtroId}
              onChange={(v) => setFiltroId(v)}
              isDisabled={tipoFiltro.id === 'TODOS'}
            />
          </Col>

          <Col md="6" className="d-flex align-items-center flex-wrap">
            <span style={{ fontSize: 11, fontWeight: 700, color: '#7f8c8d', textTransform: 'uppercase', marginRight: 12, marginTop: 24 }}>
              Tipo produto:
            </span>
            <FormGroup check inline className="mb-0 mt-4 mr-4">
              <Label check>
                <Input type="checkbox" checked={liberados} onChange={(e) => setLiberados(e.target.checked)} />
                <span className="form-check-sign" />Liberados
              </Label>
            </FormGroup>
            <FormGroup check inline className="mb-0 mt-4">
              <Label check>
                <Input type="checkbox" checked={controlados} onChange={(e) => setControlados(e.target.checked)} />
                <span className="form-check-sign" />Controlados
              </Label>
            </FormGroup>
          </Col>
        </Row>

        <hr className="my-2" />

        {/* Linha 3: Preço base */}
        <Row className="align-items-center mb-2">
          <Col md="12" className="d-flex align-items-center flex-wrap">
            <span style={{ fontSize: 11, fontWeight: 700, color: '#7f8c8d', textTransform: 'uppercase', marginRight: 12 }}>
              Preço base:
            </span>
            {PRECO_BASE_OPTS.map((opt) => (
              <div key={opt.value} className="form-check-radio form-check-inline mb-0 mr-4">
                <Label className="form-check-label">
                  <Input type="radio" name="emMassaPrecoBase"
                    id={`precoBase_${opt.value}`} value={opt.value}
                    checked={precoBase === opt.value}
                    onChange={() => setPrecoBase(opt.value)} />
                  {opt.label}
                  <span className="form-check-sign" />
                </Label>
              </div>
            ))}
          </Col>
        </Row>

        <hr className="my-2" />

        {/* Linha 4: Vencimento · Especificar Lotes */}
        <Row className="align-items-center">
          <Col md="12" className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#7f8c8d', textTransform: 'uppercase' }}>
              Vencimento:
            </span>
            <FormGroup check inline className="mb-0">
              <Label check>
                <Input type="checkbox" checked={vencerAtivo}
                  onChange={(e) => setVencerAtivo(e.target.checked)} />
                <span className="form-check-sign" />Filtrar a vencer em
              </Label>
            </FormGroup>
            <Input
              type="number" min={1} max={365}
              value={vencerDias}
              onChange={(e) => setVencerDias(parseInt(e.target.value) || 30)}
              disabled={!vencerAtivo}
              style={{ width: 60, display: 'inline-block', textAlign: 'center', fontSize: 13 }}
            />
            <span style={{ fontSize: 12.5, color: '#7f8c8d' }}>dias</span>

            <span style={{ borderLeft: '1px solid #e2e8f0', height: 20, margin: '0 4px' }} />

            <FormGroup check inline className="mb-0">
              <Label check style={{ fontWeight: 700, color: especificarLotes ? '#e74c3c' : undefined }}>
                <Input type="checkbox" checked={especificarLotes}
                  disabled={loadingSearch}
                  onChange={(e) => {
                    setEspecificarLotes(e.target.checked);
                    setSelecionados(new Set());
                    setLista([]);
                    setPesquisou(false);
                  }} />
                <span className="form-check-sign" />Especificar lotes
              </Label>
            </FormGroup>
          </Col>
        </Row>

      </div>

      {/* ── Tabela ── */}
      {loadingSearch && (
        <div style={{ padding: '40px 0', textAlign: 'center' }}><Loading tipo="componente" /></div>
      )}

      {!loadingSearch && pesquisou && (
        <>
          {/* barra superior da tabela */}
          <Row className="align-items-center mb-2">
            <Col md="6">
              <span style={{ fontSize: 12, color: '#64748b' }}>
                <strong>{lista.length}</strong> {especificarLotes ? 'lote' : 'produto'}{lista.length !== 1 ? 's' : ''} encontrado{lista.length !== 1 ? 's' : ''}
              </span>
              {selecionados.size > 0 && (
                <span style={{ marginLeft: 10, color: '#e74c3c', fontWeight: 700, fontSize: 12 }}>
                  · {selecionados.size} selecionado{selecionados.size !== 1 ? 's' : ''}
                </span>
              )}
            </Col>
            <Col md="6" className="d-flex align-items-center justify-content-end" style={{ gap: 8, flexWrap: 'nowrap' }}>
              <FormGroup check inline className="mb-0 mr-2" style={{ whiteSpace: 'nowrap' }}>
                <Label check style={{ fontSize: 12 }}>
                  <Input type="checkbox" checked={apenasMarcados}
                    onChange={(e) => setApenasMarcados(e.target.checked)} />
                  <span className="form-check-sign" />Apenas marcados
                </Label>
              </FormGroup>
              <Button color="default" size="sm" onClick={marcarTodos} style={{ whiteSpace: 'nowrap' }}>
                <i className="fa fa-check-square-o mr-1" />Marcar todos
              </Button>
              <Button color="default" size="sm" onClick={desmarcarTodos} style={{ whiteSpace: 'nowrap' }}>
                <i className="fa fa-square-o mr-1" />Desmarcar todos
              </Button>
            </Col>
          </Row>

          {listaVisivel.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#7f8c8d', fontSize: 13 }}>
              {apenasMarcados ? 'Nenhum item marcado.' : 'Nenhum produto encontrado.'}
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: '54vh', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
              <table className="table table-sm em-table" style={{ marginBottom: 0, fontSize: 12 }}>
                <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 34, textAlign: 'center' }}>
                      <input type="checkbox" className="em-check"
                        checked={todosVisveisMarcados}
                        onChange={(e) => e.target.checked ? marcarTodos() : desmarcarTodos()} />
                    </th>
                    <th style={{ width: 65 }}>Código</th>
                    <th className="em-col-produto">Produto</th>
                    <th style={{ width: 60, textAlign: 'center' }}>Saldo</th>
                    <th style={{ width: 46, textAlign: 'center' }}>Curva</th>
                    {especificarLotes && <>
                      <th style={{ width: 55, textAlign: 'center' }}>Saldo Lote</th>
                      <th style={{ width: 80, textAlign: 'center' }}>Lote</th>
                      <th style={{ width: 90, textAlign: 'center' }}>Vencimento</th>
                    </>}
                    <th style={{ width: 88, textAlign: 'right' }}>Últ. Compra</th>
                    <th style={{ width: 88, textAlign: 'right' }}>Pr. Venda</th>
                    <th style={{ width: 88, textAlign: 'right' }}>Pr. Promoção</th>
                    <th style={{ width: 105, textAlign: 'right' }}>Pr. Campanha</th>
                  </tr>
                </thead>
                <tbody>
                  {listaVisivel.map((prod) => {
                    const key = rowKey(prod);
                    const sel = selecionados.has(key);
                    const cs  = CURVA_STYLE[prod.curva_classe] || CURVA_STYLE.D;
                    const vc  = especificarLotes ? vencColor(prod.lote_dias) : null;
                    return (
                      <tr key={key}
                        className={`em-tr${sel ? ' em-tr-sel' : ''}`}
                        onClick={() => toggleSel(key)}>
                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" className="em-check"
                            checked={sel} onChange={() => toggleSel(key)} />
                        </td>
                        <td style={{ color: '#94a3b8', fontSize: 11 }}>{prod.produto_id}</td>
                        <td className="em-col-produto">
                          <div style={{ fontWeight: 600, color: '#2c3e50', lineHeight: 1.2 }}>{prod.produto}</div>
                          {prod.cod_barras && <div style={{ fontSize: 10, color: '#94a3b8' }}>{prod.cod_barras}</div>}
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: prod.saldo > 0 ? '#065f46' : '#94a3b8' }}>
                          {fmtInt(prod.saldo)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {prod.curva_classe
                            ? <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 11, fontWeight: 800, background: cs.bg, color: cs.color }}>{prod.curva_classe}</span>
                            : '—'}
                        </td>
                        {especificarLotes && <>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#0369a1' }}>
                            {fmtInt(prod.lote_saldo)}
                          </td>
                          <td style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 11 }}>
                            {prod.lote || '—'}
                          </td>
                          <td style={{ textAlign: 'center', ...vc }}>
                            {prod.lote_vencimento || '—'}
                          </td>
                        </>}
                        <td style={{ textAlign: 'right', color: '#64748b' }}>
                          {prod.ult_compra > 0 ? `R$ ${fmtNum(prod.ult_compra)}` : '—'}
                        </td>
                        <td style={{ textAlign: 'right', color: '#2c3e50' }}>R$ {fmtNum(prod.pr_venda)}</td>
                        <td style={{ textAlign: 'right', color: prod.pr_promocao > 0 ? '#0369a1' : '#94a3b8' }}>
                          {prod.pr_promocao > 0 ? `R$ ${fmtNum(prod.pr_promocao)}` : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>R$</span>
                            <input
                              type="number" min={0} step="0.01"
                              className="em-price"
                              value={parseFloat((prCampanha[priceKey(prod)] ?? prod.pr_campanha).toFixed(2))}
                              onChange={(e) => {
                                const v = Math.round((parseFloat(e.target.value) || 0) * 100) / 100;
                                const pk = priceKey(prod);
                                setPrCampanha((prev) => ({ ...prev, [pk]: v }));
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Rodapé ── */}
          {lista.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 10, marginTop: 12,
              padding: '10px 14px', background: '#f8fafc',
              border: '1px solid #e2e8f0', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#7f8c8d', textTransform: 'uppercase' }}>
                  Alterar ({selecionados.size}):
                </span>
                {['FIXO', 'PERCENTUAL'].map((tipo) => (
                  <label key={tipo} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, cursor: 'pointer' }}>
                    <input type="radio" name="emMassaAlterarTipo"
                      style={{ accentColor: '#e74c3c', cursor: 'pointer' }}
                      checked={alterarTipo === tipo}
                      onChange={() => setAlterarTipo(tipo)} />
                    {tipo === 'FIXO' ? 'Valor fixo' : 'Percentual'}
                  </label>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {alterarTipo === 'FIXO' && <span style={{ fontSize: 10, color: '#94a3b8' }}>R$</span>}
                  <input type="number" min={0} step={alterarTipo === 'FIXO' ? '0.01' : '1'}
                    value={alterarValor} onChange={(e) => setAlterarValor(e.target.value)}
                    placeholder="0"
                    style={{ width: 72, border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 7px', fontSize: 12, textAlign: 'right', outline: 'none' }} />
                  {alterarTipo === 'PERCENTUAL' && <span style={{ fontSize: 10, color: '#94a3b8' }}>%</span>}
                </div>
                <button type="button" onClick={aplicarAlteracao}
                  disabled={!selecionados.size}
                  style={{
                    background: selecionados.size ? '#e74c3c' : '#e2e8f0',
                    color: selecionados.size ? '#fff' : '#94a3b8',
                    border: 'none', borderRadius: 5, padding: '5px 12px', fontSize: 12, fontWeight: 700,
                    cursor: selecionados.size ? 'pointer' : 'not-allowed',
                  }}>
                  Aplicar diminuição
                </button>
              </div>

              <Button color="success" onClick={salvar}
                disabled={loadingSave || !selecionados.size}
                style={{ minWidth: 150 }}>
                {loadingSave
                  ? <><i className="fa fa-spinner fa-spin mr-1" />Salvando...</>
                  : <><i className="fa fa-check mr-1" />Salvar {selecionados.size > 0 ? `(${selecionados.size})` : ''}</>}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Estado inicial */}
      {!pesquisou && !loadingSearch && (
        <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 13 }}>
          <i className="fa fa-filter" style={{ fontSize: 26, display: 'block', marginBottom: 8, color: '#dfe6e9' }} />
          {!filial ? 'Selecione uma loja e clique em Pesquisar.' : 'Configure os filtros e clique em Pesquisar.'}
        </div>
      )}
    </div>
  );
};

export default EmMassaTab;
